import React, { useEffect, useRef, useState } from 'react';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { NoData } from '../MarketEffect.styles';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import {
	ChartWrapper,
	LoadingContainer,
	PriceIndexWrapper,
	SpendIndexWrapper,
} from './Charts.styles';

Highcharts.dateFormats = {
	q: function(timestamp) {
		const date = new Date(timestamp),
			quarter = Math.floor(date.getUTCMonth() / 3) + 1;
		return quarter;
	},
};

const priceChartInitialOptions = {
	chart: {
		type: 'line',
		height: '30%',
	},
	exporting: {
		enabled: false,
		allowHTML: true,
	},
	credits: {
		enabled: false,
	},
	title: {
		text: '',
	},
	legend: {
		align: 'center',
		padding: 0,
	},
	xAxis: {
		type: 'datetime',
		units: [['month', [3, 6]], ['year', [1]]],
		title: {
			text: 'Year and Quarter',
		},
		lineColor: '#000000',
		labels: {
			format: "{value:Q%q'%y}",
			rotation: -30,
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
	},
	yAxis: {
		title: {
			text: 'Weight Index',
		},
		gridLineWidth: 0,
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		lineColor: '#000000',
		lineWidth: 1,
	},
	tooltip: {
		shared: true,
		useHTML: true,
	},
	plotOptions: {
		column: {
			pointPadding: 0.2,
			borderWidth: 0,
		},
	},
	colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
};

const spendChartInitialOptions = {
	chart: {
		type: 'line',
		height: '30%',
	},
	exporting: {
		enabled: false,
		allowHTML: true,
	},
	credits: {
		enabled: false,
	},
	title: {
		text: '',
	},
	legend: {
		align: 'center',
		padding: 0,
	},
	xAxis: {
		type: 'datetime',
		units: [['month', [3, 6]], ['year', [1]]],
		title: {
			text: 'Year and Quarter',
		},
		lineColor: '#000000',
		labels: {
			format: "{value:Q%q'%y}",
			rotation: -30,
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
	},
	yAxis: {
		title: {
			text: 'Spend Index',
		},
		gridLineWidth: 0,
		labels: {
			style: {
				fontSize: '11px',
				fontWeight: '400',
				lineHeight: '13px',
				letterSpacing: 0,
				color: '#333333',
			},
		},
		lineColor: '#000000',
		lineWidth: 1,
	},
	tooltip: {
		shared: true,
		useHTML: true,
	},
	plotOptions: {
		column: {
			pointPadding: 0.2,
			borderWidth: 0,
		},
	},
	colors: ['#032E45', '#448EB4', '#45B9F7', '#AFE1FC'],
};

const Quarterly = ({ chartData, isChartLoading }) => {
	const [data, setData] = useState([]);
	const [priceIndexChartOptions, setPriceIndexChartOptions] = useState({});
	const [spendIndexChartOptions, setSpendIndexChartOptions] = useState({});
	const priceIndexRefQuarterly = useRef();
	const spendIndexRefQuarterly = useRef();

	useEffect(
		() => {
			if (chartData.length > 0) {
				const sortedData = sortByYearAndMonth(chartData);
				const marketAnalysisPriceIndexChartData = getRequiredSeriesConfig(
					sortedData,
					'total_price_index'
				);
				const marketAnalysisSpendIndexChartData = getRequiredSeriesConfig(
					sortedData,
					'total_spend_index'
				);
				setPriceIndexChartOptions({
					...priceChartInitialOptions,
					...{ series: marketAnalysisPriceIndexChartData },
				});
				setSpendIndexChartOptions({
					...spendChartInitialOptions,
					...{ series: marketAnalysisSpendIndexChartData },
				});
				setData(chartData);
			}
		}, // eslint-disable-next-line
		[chartData]
	);

	const sortByYearAndMonth = data => {
		return data.sort((a, b) => {
			if (a.pO_Year === b.pO_Year) {
				return a.pO_Month - b.pO_Month;
			}
			return a.pO_Year - b.pO_Year;
		});
	};

	const getRequiredSeriesConfig = (data, columnName) => {
		const groupedData = data.reduce((acc, item) => {
			if (!acc[item.supplier]) {
				acc[item.supplier] = [];
			}
			acc[item.supplier].push(item);
			return acc;
		}, {});

		const series = Object.keys(groupedData).map(supplier => {
			const supplierData = groupedData[supplier];
			const data = supplierData.map(item => item[columnName]);

			// get earliest year and quarter for pointStart
			const earliestData = supplierData[0];
			const pointStart = Date.UTC(
				earliestData.pO_Year,
				(earliestData.po_Quarter - 1) * 3,
				1
			);

			return {
				name: supplier,
				data: data,
				pointInterval: 3 * 30 * 24 * 3600 * 1000, // 3 months in milliseconds
				pointStart: pointStart,
			};
		});

		const highchartsConfig = {
			series: series,
		};

		return highchartsConfig.series;
	};

	const Loader = () => (
		<LoadingContainer>
			<ApLoaderDefault loaderText={() => <ApLoaderText />} />
		</LoadingContainer>
	);

	return (
		<div>
			{isChartLoading ? (
				<Loader />
			) : (
				<>
					{data.length === 0 ? (
						<NoData>No data available.</NoData>
					) : (
						<ChartWrapper>
							<PriceIndexWrapper>
								<HighchartsReact
									highcharts={Highcharts}
									options={priceIndexChartOptions}
									ref={priceIndexRefQuarterly}
								/>
							</PriceIndexWrapper>
							<SpendIndexWrapper>
								<div>
									<HighchartsReact
										highcharts={Highcharts}
										options={spendIndexChartOptions}
										ref={spendIndexRefQuarterly}
									/>
								</div>
							</SpendIndexWrapper>
						</ChartWrapper>
					)}
				</>
			)}
		</div>
	);
};

export default React.memo(Quarterly);
